import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useLocationSave } from "@/queries/use-locations.query.ts";
import { CountrySelect } from "@/components/select/CountrySelect.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import getTimezones from "@/services/timezone-service.ts";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import { ApiLocation, ApiLocationSavePayload } from "@/types/location.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Modal, ModalProps, Select, Stack, useModals } from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface LocationEditModalProps extends ModalProps {
  location?: ApiLocation;
}

export function LocationEditModal({ location, ...props }: LocationEditModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const isEditMode = !!location;

  const requiredMessage = t("errors:field_required");
  const locationSchema = z.object({
    title: z.string().min(1, requiredMessage),
    address_1: z.string().nullable().optional(),
    address_2: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    zip: z.string().nullable().optional(),
    country_id: z.number({ error: requiredMessage }),
    timezone: z.string({ error: requiredMessage }).min(1, requiredMessage),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(locationSchema),
    defaultValues: {
      ...location,
      country_id: location?.country.id,
    },
  });

  const locationSave = useLocationSave(control);
  const { addRecent } = useRecent();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload: ApiLocationSavePayload = {
        title: data.title,
        address_1: data.address_1,
        address_2: data.address_2,
        city: data.city,
        state: data.state,
        zip: data.zip,
        country_id: data.country_id,
        timezone: data.timezone,
      };
      const savedData = await locationSave.mutateAsync({ id: location?.id ?? 0, payload: payload });
      if (dirtyFields.country_id) {
        addRecent([{ type: ApiRecentKey.country, values: [data.country_id] }]);
      }
      if (isEditMode) {
        analyticsService.trackEvent(analyticEvents.locations.edited, {
          [analyticProperties.id]: location.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.locations.created, {
          [analyticProperties.id]: savedData.id,
        });
      }
      close(id);
    } catch {
      return;
    }
  });
  return (
    <>
      <Modal
        {...props}
        title={t("common:location")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              type={"submit"}
              size={"lg"}
              isLoading={locationSave.isPending}
              onClick={onSubmit}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Input
            required={true}
            label={t("common:title")}
            autoFocus={true}
            type="text"
            {...register("title")}
            error={errors.title?.message}
          />
          <Input
            label={t("core:address_line_1")}
            type={"text"}
            {...register("address_1", {
              value: undefined,
            })}
            error={errors.address_1?.message}
          />
          <Input
            label={t("core:address_line_2")}
            type={"text"}
            {...register("address_2", {
              value: undefined,
            })}
            error={errors.address_2?.message}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <CountrySelect
                emptyTitle={t("core:country")}
                searchable={true}
                required
                value={value}
                onChange={onChange}
                error={errors.country_id?.message}
              />
            )}
            control={control}
            name={"country_id"}
          />
          <Input
            label={t("core:state")}
            type={"text"}
            {...register("state", {
              value: undefined,
            })}
            error={errors.state?.message}
          />
          <Input
            label={t("core:city")}
            type={"text"}
            {...register("city", {
              value: undefined,
            })}
            error={errors.city?.message}
          />
          <Input
            label={t("core:zip")}
            type={"text"}
            {...register("zip", {
              value: undefined,
            })}
            error={errors.zip?.message}
          />
          <Controller
            control={control}
            name="timezone"
            render={({ field: { value, onChange } }) => (
              <Select
                required
                label={t("common:timezone")}
                options={getTimezones()}
                emptyTitle={t("common:select")}
                value={value}
                onChange={onChange}
                searchable
                error={errors.timezone?.message}
              />
            )}
          />
        </form>
      </Modal>
    </>
  );
}
